import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getQueryKey } from '@trpc/react-query';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import SaveAlt from '@mui/icons-material/SaveAlt';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Replay from '@mui/icons-material/Replay';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useFlag } from '@reflag/react-sdk';
import {
  createLpCapitalActionEditRoute,
  createLpCapitalActionNoticeCreateRoute,
} from 'adminConstants/routes';
import { useLayoutContext } from 'contexts/LayoutContext';
import { differenceInDays } from 'date-fns';
import { ActionStatus } from 'further-types/lp/action';
import { CapitalActionNoticeStatus } from 'further-types/lp/capital-action-notice';
import { CalendarDayRange } from 'further-types/utils';
import ColorIndicator from 'further-ui/components/ColorIndicator';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import DateRangePicker from 'further-ui/components/DateRangePicker';
import { Column, Row } from 'further-ui/components/Layout';
import SearchField from 'further-ui/components/SearchField';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { useDisclosure, useFilters } from 'further-ui/hooks';
import { ActionStatusLabels } from 'further-ui/labels';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import PageFilters, { Filter } from 'components/new/PageFilters';
import api from 'lib/trpcClient';
import CapitalActionCapitalProgress from './CapitalActionCapitalProgress';
import DownloadCapitalNotices from './DownloadCapitalNotices';

const NET_DISTRIBUTION_COLOR = '#C0C0C0';
const NET_CALL_COLOR = '#CFB53B';

type CapitalAction = {
  id: string;
  name: string;
  firmName: string;
  investors: Array<{
    id: string;
    fullName: string;
  }>;
  capitalNotice: {
    noticeDate?: CalendarDay;
    investorPdfUploads: Array<{
      investorId: string;
      path: string;
      previewUrl: string;
    }>;
  } | null;
};

const DateRangeFilter = styled(Box)(({ theme }) => ({
  flex: 1,
  maxWidth: '275px',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
  },
}));

const ExpandButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(theme.spacingSizes.xs),
}));

const StyledConfirmDialog = styled(ConfirmDialog)({
  '& .MuiDialogContent-root': {
    paddingBottom: '0px !important',
  },
});

const CapitalActions: FC = () => {
  const history = useHistory();
  const { selectedVehicleId, vehicle } = useLayoutContext();
  const vehicleId = selectedVehicleId ?? '';
  const baseCurrency = vehicle?.baseCurrency;
  const { toggleRowExpansion, expandedRows } = useExpandedRows();
  const recallCapitalNoticesDisclosure = useDisclosure<{ id: string }>();
  const [capitalAction, setCapitalAction] = useState<CapitalAction | null>(
    null,
  );
  const { isEnabled: showBankRecDataInCapitalNotices } = useFlag(
    'show-bank-rec-data-in-capital-notices',
  );
  const { isEnabled: showEditCapitalActionInCapitalNotices } = useFlag(
    'show-edit-capital-action-in-capital-notices',
  );

  const { filters, handleChangeFilters } = useFilters<{
    investorName: string;
    actionDueDateRange?: CalendarDayRange;
  }>(`capital-actions-${vehicleId}`, {
    defaultFilters: {
      investorName: '',
    },
  });

  const [capitalActions] = api.capitalAction.list.useSuspenseQuery(
    { vehicleId },
    {
      staleTime: 0,
      refetchOnWindowFocus: true,
    },
  );

  const filteredCapitalActions = capitalActions
    .filter((capitalAction) =>
      capitalAction.investors
        .map((investor) => investor.fullName)
        .some((investorName) =>
          investorName
            .toLowerCase()
            .includes(filters.investorName.toLowerCase()),
        ),
    )
    .filter((capitalAction) =>
      filters.actionDueDateRange
        ? capitalAction.actionDueDate.isWithinRange(filters.actionDueDateRange)
        : true,
    );

  const recallCapitalActionNotice = api.capitalActionNotice.recall.useMutation(
    useHandleTrpcMutation()({
      invalidations: [getQueryKey(api.capitalAction.list, { vehicleId })],
      onSuccess: () => {
        recallCapitalNoticesDisclosure.onClose();
      },
    }),
  );

  const handleRecallCapitalActionNotice = () => {
    recallCapitalActionNotice.mutate({
      vehicleId,
      capitalActionNoticeId: recallCapitalNoticesDisclosure.actionProps.id,
    });
  };

  const columns: Array<TableColumn<(typeof filteredCapitalActions)[number]>> = [
    {
      label: 'Action name',
      key: 'name',
      noWrap: true,
      render: ({ name, isNetDistribution }) => (
        <Row spacing="sm" centered>
          <ColorIndicator
            color={isNetDistribution ? NET_DISTRIBUTION_COLOR : NET_CALL_COLOR}
          />
          <Typography variant="h6">{name}</Typography>
        </Row>
      ),
    },
    {
      label: 'Status',
      key: 'status',
      noWrap: true,
      render: ({ status, capitalNotice }) => {
        if (!capitalNotice) {
          return ActionStatusLabels[status];
        }

        if (capitalNotice.status === CapitalActionNoticeStatus.Draft) {
          return 'Pending notice';
        } else if (
          capitalNotice.status === CapitalActionNoticeStatus.Published
        ) {
          return 'Notice sent';
        }

        return ActionStatusLabels[status];
      },
    },
    {
      label: 'Due date',
      key: 'actionDueDate',
      noWrap: true,
      render: ({ actionDueDate }) => {
        const today = new CalendarDay(new Date());
        const daysUntilDue = differenceInDays(
          actionDueDate.toUTCDate(),
          today.toUTCDate(),
        );
        const daysUntilDueColor = daysUntilDue <= 7 ? 'error' : 'grey.900';

        return (
          <Typography variant="h6" color={daysUntilDueColor}>
            {actionDueDate.toReadable()}
          </Typography>
        );
      },
    },
    {
      label: 'Net call / (distribution)',
      key: 'netCallDistribution',
      render: ({ netCallDistribution }) =>
        numberToCurrencyString(netCallDistribution, {
          currency: baseCurrency,
        }),
    },
    {
      label: 'Capital call',
      key: 'capitalCall',
      render: ({ capitalCallTotal }) =>
        numberToCurrencyString(capitalCallTotal, {
          currency: baseCurrency,
        }),
    },
    {
      label: 'Distribution',
      key: 'distribution',
      render: ({ distributionTotal }) =>
        numberToCurrencyString(distributionTotal, {
          currency: baseCurrency,
        }),
    },
    ...(showBankRecDataInCapitalNotices
      ? [
          {
            label: 'Investors paid',
            key: 'investorsPaid',
            render: ({
              status,
              isNetDistribution,
              investorsPaid,
              totalInvestors,
            }) => {
              if (status === ActionStatus.Draft || isNetDistribution) {
                return 'N/A';
              }
              return `${investorsPaid}/${totalInvestors}`;
            },
          },
          {
            label: 'Amount paid',
            key: 'amountPaid',
            render: ({ status, isNetDistribution, amountPaid }) => {
              if (status === ActionStatus.Draft || isNetDistribution) {
                return 'N/A';
              }
              return numberToCurrencyString(amountPaid, {
                currency: baseCurrency,
              });
            },
          },
          {
            key: 'toggle',
            displayBehindActions: true,
            render: ({ id }) => (
              <ExpandButton>
                {expandedRows.has(id) ? (
                  <ExpandLess fontSize="small" />
                ) : (
                  <ExpandMore fontSize="small" />
                )}
              </ExpandButton>
            ),
          },
        ]
      : []),
  ];

  return (
    <Column spacing="lg">
      <PageFilters justify="space-between">
        <Filter size="md">
          <SearchField
            clearable
            fullWidth
            placeholder="Search by investor"
            value={filters.investorName}
            autoSearchOnDebounce
            allowEmptyQuery
            onSearch={(query) => {
              handleChangeFilters({ investorName: query });
            }}
          />
        </Filter>
        <DateRangeFilter>
          <DateRangePicker
            dateRange={filters.actionDueDateRange}
            onDateRangeChange={(dateRange) => {
              handleChangeFilters({ actionDueDateRange: dateRange });
            }}
            placeholder="Select due date range"
          />
        </DateRangeFilter>
      </PageFilters>
      <Table
        isLoading={false}
        columns={columns}
        rows={filteredCapitalActions}
        actions={[
          ...(showEditCapitalActionInCapitalNotices
            ? [
                {
                  label: 'Edit capital action',
                  icon: Edit,
                  disabled: ({ capitalNotice }) => !!capitalNotice?.id,
                  tooltip: ({ capitalNotice }) =>
                    capitalNotice?.id
                      ? 'This capital action cannot be edited because notices have been created.'
                      : undefined,
                  onClick: ({ id }) => {
                    history.push(createLpCapitalActionEditRoute(id));
                  },
                },
              ]
            : []),
          {
            label: ({ capitalNotice }) =>
              !capitalNotice?.id
                ? 'Create capital notices'
                : 'Edit capital notices',
            icon: Add,
            hidden: ({ status }) => status === ActionStatus.Draft,
            onClick: ({ id }) => {
              history.push(createLpCapitalActionNoticeCreateRoute(id));
            },
          },
          {
            label: 'Download capital notices',
            icon: SaveAlt,
            hidden: ({ capitalNotice }) =>
              !capitalNotice?.id ||
              capitalNotice.status !== CapitalActionNoticeStatus.Published,
            onClick: (capitalAction) => setCapitalAction(capitalAction),
          },
          {
            label: 'Recall capital notices',
            icon: Replay,
            hidden: ({ capitalNotice }) =>
              !capitalNotice?.id ||
              capitalNotice.status !== CapitalActionNoticeStatus.Published,
            onClick: ({ capitalNotice }) => {
              if (capitalNotice?.id) {
                recallCapitalNoticesDisclosure.stageAction({
                  id: capitalNotice.id,
                });
              }
            },
          },
        ]}
        isExpandedRow={({ id }) => expandedRows.has(id)}
        getExpandedContent={(capitalAction, tableWidth) => (
          <CapitalActionCapitalProgress
            capitalAction={capitalAction}
            tableWidth={tableWidth}
            baseCurrency={baseCurrency}
          />
        )}
        onRowClick={
          showBankRecDataInCapitalNotices
            ? (row) => {
                toggleRowExpansion(row.id);
              }
            : undefined
        }
        emptyMessage="No capital actions found"
      />
      {capitalAction ? (
        <DownloadCapitalNotices
          firmName={capitalAction.firmName}
          capitalActionName={capitalAction.name}
          capitalNotice={capitalAction.capitalNotice}
          investors={capitalAction.investors}
          onClose={() => setCapitalAction(null)}
        />
      ) : null}
      <StyledConfirmDialog
        open={recallCapitalNoticesDisclosure.isOpen}
        onClose={recallCapitalNoticesDisclosure.onClose}
        title="Recalling capital notices"
        maxWidth="sm"
        content={
          <Typography variant="body2">
            This cannot be undone. If you need notices in future, new ones must
            be created. An email will be sent to all investors that were
            originally notified by email about this notice, explaining that the
            notice has been recalled.
          </Typography>
        }
        loading={recallCapitalActionNotice.isPending}
        actions={{
          confirm: {
            label: 'Recall notices',
            variant: 'contained',
            color: 'primary',
            onClick: handleRecallCapitalActionNotice,
          },
        }}
      />
    </Column>
  );
};

export default CapitalActions;
